// CartItemStockRow — DEV-50924.
// Per-line pill + earliest-date row rendered under the SKU/price line on the
// cart. The resolver in utils/cartItemStock.ts decides the state; this
// component is pure presentational and reuses the shared badge components so
// the cart visually matches Search / ProductDetail.

import { Clock } from 'lucide-react'
import { BackorderBadge } from './BackorderBadge'
import { CheckingStockBadge } from './CheckingStockBadge'
import { InStockBadge } from './InStockBadge'
import type { CartItemStockView } from '../../utils/cartItemStock'

export interface CartItemStockRowProps {
  view: CartItemStockView
}

function renderPill(pill: CartItemStockView['pill']) {
  switch (pill) {
    case 'in_stock':
      return <InStockBadge />
    case 'backorder':
      // DEV-51018: amber variant matches the engineer mock — a clear
      // Backorder pill sits alongside the SLA-date row so the line reads
      // distinctly from its in-stock siblings.
      return <BackorderBadge variant="amber" />
    case 'checking':
      return <CheckingStockBadge />
  }
}

export function CartItemStockRow({ view }: CartItemStockRowProps) {
  // DEV-51018: backorder dates are estimates — prefix "Est." so the
  // engineer reads them as an SLA rather than a promise. In-stock rows
  // stay as-is (their dates are firm branch/DC availability).
  const dateText =
    view.dateLabel != null && view.pill === 'backorder'
      ? `Est. ${view.dateLabel}`
      : view.dateLabel
  return (
    <div className="mt-2 flex items-center gap-2 text-xs text-gray-600">
      {renderPill(view.pill)}
      {dateText != null && (
        <span className="inline-flex items-center gap-1">
          <Clock className="w-3.5 h-3.5" aria-hidden="true" />
          <span>{dateText}</span>
        </span>
      )}
    </div>
  )
}
